import React from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import { Helmet } from "react-helmet-async";
import { Surface } from "../../atoms/Surface";
import {
  HiCheckCircle,
  HiMail,
  HiChatAlt2,
  HiOutlineHome,
  HiCalendar,
} from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { CustomButton } from "../../atoms/CustomButton";

const ROIGuaranteeSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleWhatsAppClick = () => {
    window.open("https://wa.me/41783145598", "_blank");
  };

  const handleEmailClick = () => {
    window.open("mailto:info@super-user-manager.com", "_blank");
  };

  const handleScheduleMeetingClick = () => {
    const currentLanguage = i18n.language || "en";
    const scheduleLink = currentLanguage.startsWith("fr")
      ? "https://tidycal.com/pinkswan/super-user-manager-fr"
      : "https://tidycal.com/pinkswan/super-user-manager";
    window.open(scheduleLink, "_blank");
  };

  return (
    <div className="roi-guarantee-success-page">
      <Helmet>
        <title>
          {t("dashboard.extension_pack.page_title")} | Super User Manager
        </title>
        <meta
          name="description"
          content={t("dashboard.extension_pack.page_description") || ""}
        />
      </Helmet>

      <div className="container mx-auto py-6 pt-20">
        <div className="max-w-4xl mx-auto">
          <Surface className="mb-8 bg-green-50 border-green-200">
            <div className="text-center py-8">
              <HiCheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {t("dashboard.extension_pack.title")}
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t("dashboard.extension_pack.description")}
              </p>
            </div>
          </Surface>

          <Surface className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              {t("dashboard.extension_pack.what_next_title")}
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-blue-600 font-semibold text-sm">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {t("dashboard.extension_pack.schedule_title")}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {t("dashboard.extension_pack.schedule_desc")}
                  </p>
                  <CustomButton
                    variant="primary"
                    onClick={handleScheduleMeetingClick}
                    className="inline-flex"
                    icon={<HiCalendar className="w-4 h-4" />}
                    title={t("dashboard.extension_pack.schedule_button")}
                  />
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-blue-600 font-semibold text-sm">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {t("dashboard.extension_pack.step1_title")}
                  </h3>
                  <p className="text-gray-600">
                    {t("dashboard.extension_pack.step1_desc")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-blue-600 font-semibold text-sm">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {t("dashboard.extension_pack.step2_title")}
                  </h3>
                  <p className="text-gray-600">
                    {t("dashboard.extension_pack.step2_desc")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-blue-600 font-semibold text-sm">4</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {t("dashboard.extension_pack.step3_title")}
                  </h3>
                  <p className="text-gray-600">
                    {t("dashboard.extension_pack.step3_desc")}
                  </p>
                </div>
              </div>
            </div>
          </Surface>

          <Surface className="mb-8 bg-blue-50 border-blue-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              {t("dashboard.extension_pack.contact_title")}
            </h2>
            <p className="text-gray-700 mb-6">
              {t("dashboard.extension_pack.contact_desc")}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <HiChatAlt2 className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {t("dashboard.extension_pack.whatsapp_title")}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {t("dashboard.extension_pack.whatsapp_desc")}
                  </p>
                  <CustomButton
                    variant="custom"
                    onClick={handleWhatsAppClick}
                    className="inline-flex bg-green-600 text-white enabled:hover:bg-green-700"
                    icon={<HiChatAlt2 className="w-4 h-4" />}
                    title={t("dashboard.extension_pack.whatsapp_button")}
                  />
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <HiMail className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {t("dashboard.extension_pack.email_title")}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {t("dashboard.extension_pack.email_desc")}
                  </p>
                  <CustomButton
                    variant="primary"
                    onClick={handleEmailClick}
                    className="inline-flex"
                    icon={<HiMail className="w-4 h-4" />}
                    title={t("dashboard.extension_pack.email_button")}
                  />
                </div>
              </div>
            </div>
          </Surface>

          <div className="text-center">
            <CustomButton
              variant="primary"
              size="lg"
              onClick={() => navigate("/control-panel")}
              className="inline-flex"
              icon={<HiOutlineHome className="w-5 h-5" />}
              title={t("dashboard.extension_pack.back_to_dashboard")}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROIGuaranteeSuccess;
